import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Grid, GridItem, Skeleton, Text} from '@chakra-ui/react'
import {ChevronRightIcon} from '@chakra-ui/icons'
import i18next from 'i18next'
import {useTranslation} from 'react-i18next'
import {toLocalDate} from '~/utils/formatter'
import BankLogo from '~/react/components/BankLogo'
import PaymentStatus from '../../PaymentStatus'

const PaymentCard = ({payment, onClickPayment}) => {
  i18next.loadNamespaces('pointsOnPayments')
  const {t} = useTranslation('pointsOnPayments')

  const {
    payee,
    paymentReference,
    createdAt,
    status,
    netPaymentAmount,
    grossPaymentAmount,
    paymentMethod,
  } = payment

  const debitAgreement = paymentMethod?.debitAgreement
  const bankName = debitAgreement?.bankName
  const lastFour = debitAgreement?.accountNumber?.slice(-4)
  const feeAmount = grossPaymentAmount.subtract(netPaymentAmount)

  const formattedDate = toLocalDate(createdAt, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return (
    <Grid
      templateColumns="80px 1fr auto"
      gap="sm"
      borderTop="1px solid"
      borderColor="mld.neutral.200"
      py="sm"
      cursor="pointer"
      onClick={() => onClickPayment(payment)}
      alignItems="start"
    >
      <GridItem>
        <Text fontSize="sm" color="mld.neutral.700">
          {formattedDate}
        </Text>
      </GridItem>

      <GridItem>
        <Box display="inline-block">
          <PaymentStatus status={status} />
        </Box>

        <Text fontWeight="semibold" className="fs-mask" mt="xs">
          {payee?.name}
        </Text>
        {paymentReference && (
          <Text fontSize="sm" color="mld.neutral.600" mt="xxs">
            Ref: {paymentReference}
          </Text>
        )}
        {bankName && (
          <Flex alignItems="center" gap="xs" mt="xs">
            <BankLogo providerName={bankName} />
            {lastFour && (
              <Text fontSize="sm" color="mld.neutral.600">
                •••• {lastFour}
              </Text>
            )}
          </Flex>
        )}
        <Box mt="xs">
          <Box fontSize="xs" color="mld.neutral.500" textTransform="uppercase">
            {t(
              'pointsOnPayments:pointsOnPayment.table.columns.headers.netPaymentAmount',
            )}
          </Box>
          <Box fontSize="sm" fontWeight="medium">
            {netPaymentAmount.toFormat()}
          </Box>
        </Box>
        <Box mt="xs">
          <Box fontSize="xs" color="mld.neutral.500" textTransform="uppercase">
            {t(
              'pointsOnPayments:pointsOnPayment.table.columns.headers.feeAmount',
            )}
          </Box>
          <Box fontSize="sm" fontWeight="medium">
            {feeAmount.toFormat()}
          </Box>
        </Box>
        <Box mt="xs">
          <Box fontSize="xs" color="mld.neutral.500" textTransform="uppercase">
            {t(
              'pointsOnPayments:pointsOnPayment.table.columns.headers.grossPaymentAmount',
            )}
          </Box>
          <Box fontSize="sm" fontWeight="medium">
            {grossPaymentAmount.toFormat()}
          </Box>
        </Box>
      </GridItem>

      <GridItem>
        <ChevronRightIcon boxSize="20px" color="mld.neutral.500" />
      </GridItem>
    </Grid>
  )
}

PaymentCard.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    payee: PropTypes.shape({name: PropTypes.string}),
    paymentReference: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    netPaymentAmount: PropTypes.shape({
      toFormat: PropTypes.func,
      subtract: PropTypes.func,
    }).isRequired,
    grossPaymentAmount: PropTypes.shape({
      toFormat: PropTypes.func,
      subtract: PropTypes.func,
    }).isRequired,
    paymentMethod: PropTypes.shape({
      debitAgreement: PropTypes.shape({
        bankName: PropTypes.string,
        accountNumber: PropTypes.string,
      }),
    }),
  }).isRequired,
  onClickPayment: PropTypes.func.isRequired,
}

const MediumPaymentsTable = ({payments, isLoading, onClickPayment}) => {
  if (isLoading) {
    return (
      <Box px="sm">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="140px" mb="sm" borderRadius="md" />
        ))}
      </Box>
    )
  }

  return (
    <Box px="sm" className="fs-unmask">
      {payments.map((payment) => (
        <PaymentCard
          key={payment.id}
          payment={payment}
          onClickPayment={onClickPayment}
        />
      ))}
      <Box borderTop="1px solid" borderColor="mld.neutral.200" />
    </Box>
  )
}

MediumPaymentsTable.propTypes = {
  payments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClickPayment: PropTypes.func.isRequired,
}

export default MediumPaymentsTable
